import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { createReceipt, getOutstandingDocuments, calculateAllocationComponents } from '../../../../redux/actions/SocietyPortal/ReceiptAction/ReceiptAction';
import { getWingsDropdown, getFlatsWithOwnership } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getSocietyBanksDropdown } from '../../../../redux/actions/SocietyPortal/SocietyBankAction/SocietyBankAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getInvoiceSettings } from '../../../../redux/actions/SocietyPortal/InvoiceSettingAction/InvoiceSettingAction';
import { getChartOfAccountsDropdown } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { previewDocumentSeriesReq } from '../../../../axios/request/SocietyPortal/DocumentSeriesReqs/DocumentSeriesReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { PAYMENT_PROOF_ACCEPT, isHeicFile, validatePaymentProof } from './paymentProof';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_RECEIPTS, SOCIETY_PORTAL_SETUP_DOCUMENT_SERIES } from '../../../../Routing/routes';

const htmlToPlainText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
};

const PAYMENT_MODES = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'DD', label: 'Demand Draft' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'UPI', label: 'UPI' },
    { value: 'NEFT', label: 'NEFT' },
    { value: 'RTGS', label: 'RTGS' },
    { value: 'ONLINE', label: 'Online' },
];

const RECEIPT_TYPES = [
    { value: 'PAYMENT', label: 'Payment' },
    { value: 'ADVANCE', label: 'Advance' },
];

const requiresInstrumentDetails = (mode) => ['CHEQUE', 'DD'].includes(mode);

// Societies use the same TDS receivable account nearly every time, so remember the last pick
const tdsGlStorageKey = (societyId) => `tds_gl_code:${societyId}`;

const CreateReceipt = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { wingsDropdown, flatsWithOwnership, flatsWithOwnershipLoading } = useSelector(state => state.WingsAndFlatsReducers || {});
    const { createLoading, outstandingInvoices, outstandingDebitNotes, outstandingLoading, allocationComponents, componentsLoading } = useSelector(state => state.ReceiptReducer || {});
    const { banksDropdown, banksDropdownLoading } = useSelector(state => state.SocietyBankReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { invoiceSettings } = useSelector(state => state.InvoiceSettingReducer || {});
    const { coaDropdown, coaDropdownLoading } = useSelector(state => state.JournalReducer || {});
    const receiptNotePrefilledRef = useRef(false);

    const [formData, setFormData] = useState({
        wing_id: '',
        flat_id: '',
        entity_id: '',
        receipt_type: 'PAYMENT',
        payment_date: localDateString(),
        amount: '',
        payment_mode: 'CASH',
        bank_account_id: '',
        reference_no: '',
        bank_name: '',
        instrument_date: '',
        tds_deducted: '',
        tds_section: '',
        tds_rate: '',
        tds_gl_code: '',
        remarks: '',
    });

    const [allocations, setAllocations] = useState([]);
    const [selectedFlatMembers, setSelectedFlatMembers] = useState([]);
    const [proofFile, setProofFile] = useState(null);
    const [proofPreview, setProofPreview] = useState('');
    const proofInputRef = useRef(null);

    useEffect(() => {
        if (!proofFile) {
            setProofPreview('');
            return;
        }
        const url = URL.createObjectURL(proofFile);
        setProofPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [proofFile]);
    const prefillFlatId = searchParams.get('flat_id');
    const prefillWingId = searchParams.get('wing_id');
    const prefillInvoiceId = searchParams.get('invoice_id');
    const prefillDebitNoteId = searchParams.get('debit_note_id');
    const prefillApplied = useRef(false);
    const invoicePrefillApplied = useRef(false);
    const debitNotePrefillApplied = useRef(false);

    // Document series pre-check
    const [seriesCheck, setSeriesCheck] = useState({ loading: true, exists: false, previewNumber: null });

    useEffect(() => {
        const checkSeries = async () => {
            try {
                const documentType = formData.receipt_type === 'ADVANCE' ? 'ADVANCE' : 'RECEIPT';
                const result = await previewDocumentSeriesReq({ data: { society_id: societyId, document_type: documentType } });
                if (result.success === true) {
                    const data = result.data;
                    setSeriesCheck({ loading: false, exists: true, previewNumber: data?.preview || data?.next_number || null });
                } else {
                    setSeriesCheck({ loading: false, exists: false, previewNumber: null });
                }
            } catch {
                setSeriesCheck({ loading: false, exists: false, previewNumber: null });
            }
        };
        checkSeries();
    }, [societyId, formData.receipt_type]);

    useEffect(() => {
        dispatch(getWingsDropdown({ society_id: societyId }));
        dispatch(getSocietyBanksDropdown({ society_id: societyId }));
        dispatch(getInvoiceTypes({ society_id: societyId }));
        dispatch(getChartOfAccountsDropdown({ societyId, type: 'account' }));
    }, [dispatch, societyId]);

    // Fetch invoice settings (uses first invoice type) to prefill receipt note
    useEffect(() => {
        const firstType = (invoiceTypesData || [])[0];
        if (firstType?.invoice_type_id) {
            dispatch(getInvoiceSettings({ society_id: societyId, invoice_type_id: firstType.invoice_type_id }));
        }
    }, [dispatch, societyId, invoiceTypesData]);

    // Prefill remarks once with receipt_notes from settings
    useEffect(() => {
        if (receiptNotePrefilledRef.current) return;
        if (!invoiceSettings) return;
        if (invoiceSettings.show_receipt_note === 1) {
            const noteText = htmlToPlainText(invoiceSettings.receipt_notes);
            if (noteText) {
                setFormData(prev => ({ ...prev, remarks: noteText }));
            }
            receiptNotePrefilledRef.current = true;
        }
    }, [invoiceSettings]);

    // Auto-select wing & flat from query params (e.g. from Invoice detail)
    useEffect(() => {
        if (!prefillFlatId || !prefillWingId || prefillApplied.current) return;
        prefillApplied.current = true;
        setFormData(prev => ({ ...prev, wing_id: prefillWingId, flat_id: prefillFlatId }));
    }, [prefillFlatId, prefillWingId]);

    const skipNextReset = useRef(!!prefillFlatId);
    useEffect(() => {
        if (formData.wing_id) {
            dispatch(getFlatsWithOwnership({ society_id: societyId, wing_id: formData.wing_id }));
            if (skipNextReset.current) {
                skipNextReset.current = false;
                return;
            }
            setFormData(prev => ({ ...prev, flat_id: '', entity_id: '' }));
            setSelectedFlatMembers([]);
            setAllocations([]);
        }
    }, [dispatch, societyId, formData.wing_id]);

    useEffect(() => {
        if (formData.flat_id && flatsWithOwnership?.length) {
            const flat = flatsWithOwnership.find(f => f.flat_id === parseInt(formData.flat_id));
            const members = flat?.ownerships || [];
            setSelectedFlatMembers(members);
            if (members.length === 1) {
                setFormData(prev => ({ ...prev, entity_id: String(members[0].entity?.entity_id || '') }));
            }
        } else {
            setSelectedFlatMembers([]);
        }
    }, [formData.flat_id, flatsWithOwnership]);

    // Fetch outstanding documents for selected flat
    useEffect(() => {
        if (formData.flat_id) {
            dispatch(getOutstandingDocuments({ flat_id: formData.flat_id, society_id: societyId }));
        }
    }, [dispatch, societyId, formData.flat_id]);

    // Arriving from an invoice: select it and pre-fill its outstanding balance once the
    // flat's outstanding documents land. Runs once so later edits are not overwritten.
    useEffect(() => {
        if (!prefillInvoiceId || invoicePrefillApplied.current || outstandingLoading) return;

        const invoice = (outstandingInvoices || []).find(inv => String(inv.invoice_id) === String(prefillInvoiceId));
        // A carried forward invoice is collected on its successor, so it is not allocatable here
        if (!invoice || invoice.is_carried_forward === 1) return;

        invoicePrefillApplied.current = true;
        const balanceDue = parseFloat(invoice.balance_due || 0);
        if (balanceDue <= 0) return;

        setAllocations([{
            target_type: 'invoice',
            target_id: String(invoice.invoice_id),
            amount: String(balanceDue)
        }]);
        setFormData(prev => (prev.amount ? prev : { ...prev, amount: String(balanceDue) }));
    }, [prefillInvoiceId, outstandingInvoices, outstandingLoading]);

    // Same, arriving from a debit note
    useEffect(() => {
        if (!prefillDebitNoteId || debitNotePrefillApplied.current || outstandingLoading) return;

        const note = (outstandingDebitNotes || []).find(n => String(n.note_id) === String(prefillDebitNoteId));
        if (!note || note.is_carried_forward === 1) return;

        debitNotePrefillApplied.current = true;
        const balanceDue = parseFloat(note.balance_due || 0);
        if (balanceDue <= 0) return;

        setAllocations([{
            target_type: 'debit_note',
            target_id: String(note.note_id),
            amount: String(balanceDue)
        }]);
        setFormData(prev => (prev.amount ? prev : { ...prev, amount: String(balanceDue) }));
    }, [prefillDebitNoteId, outstandingDebitNotes, outstandingLoading]);

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Auto-select default bank when switching to non-CASH payment mode
        let bankAutoSelect = {};
        if (name === 'payment_mode' && value !== 'CASH') {
            const defaultBank = (banksDropdown || []).find(b => b.account_type === 'BANK' && b.is_default === 1);
            if (defaultBank) {
                bankAutoSelect = { bank_account_id: String(defaultBank.id) };
            }
        } else if (name === 'payment_mode' && value === 'CASH') {
            bankAutoSelect = { bank_account_id: '' };
        }
        // TDS GL only applies while TDS is deducted. Clearing the amount clears the account
        // (the API stores null anyway); entering one falls back to this society's last pick.
        let tdsGlSync = {};
        if (name === 'tds_deducted') {
            const deducted = parseFloat(value) || 0;
            if (deducted <= 0) {
                tdsGlSync = { tds_gl_code: '' };
            } else if (!formData.tds_gl_code) {
                const remembered = localStorage.getItem(tdsGlStorageKey(societyId));
                if (remembered) tdsGlSync = { tds_gl_code: remembered };
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...bankAutoSelect,
            ...tdsGlSync,
            ...(name === 'wing_id' ? { flat_id: '', entity_id: '' } : {}),
            ...(name === 'flat_id' ? { entity_id: '' } : {}),
        }));
        if (name === 'receipt_type' && value === 'ADVANCE') {
            setAllocations([]);
        }
    };

    const handleProofSelect = (e) => {
        const file = e.target.files?.[0];
        // Reset so picking the same file again after removing it still fires onChange
        e.target.value = '';
        if (!file) return;
        const error = validatePaymentProof(file);
        if (error) {
            alert(error);
            return;
        }
        setProofFile(file);
    };

    // Allocation management
    const addAllocation = (type) => {
        setAllocations(prev => [...prev, {
            target_type: type, // 'invoice' or 'debit_note'
            target_id: '',
            amount: '',
        }]);
    };

    const updateAllocation = (index, field, value) => {
        setAllocations(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const removeAllocation = (index) => {
        setAllocations(prev => prev.filter((_, i) => i !== index));
    };

    const totalAllocated = allocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
    const receiptAmount = parseFloat(formData.amount) || 0;
    const tdsDeducted = parseFloat(formData.tds_deducted) || 0;
    // TDS is withheld by the member on top of what reaches the bank, so the member's dues are
    // settled by both together - allocations are capped on this, not on the banked amount.
    const totalSettled = receiptAmount + tdsDeducted;
    const unallocated = totalSettled - totalAllocated;
    const tdsGlRequired = tdsDeducted > 0;

    const validAllocations = allocations.filter(a => a.target_id && parseFloat(a.amount) > 0);

    const handlePreviewComponents = () => {
        if (!validAllocations.length) return;
        dispatch(calculateAllocationComponents({
            society_id: societyId,
            allocations: validAllocations.map(a => ({
                ...(a.target_type === 'invoice' ? { invoice_id: parseInt(a.target_id) } : { debit_note_id: parseInt(a.target_id) }),
                amount: parseFloat(a.amount),
            })),
        }));
    };

    // Build a lookup map from allocationComponents for display
    const componentsMap = {};
    (allocationComponents || []).forEach(c => {
        const key = c.invoice_id ? `invoice_${c.invoice_id}` : `debit_note_${c.debit_note_id}`;
        componentsMap[key] = c.components || [];
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.flat_id || !formData.entity_id || !formData.amount || !formData.payment_date) {
            alert('Please fill all required fields.');
            return;
        }

        if (formData.payment_mode !== 'CASH' && !formData.bank_account_id) {
            alert('Please select a bank account for non-cash payments.');
            return;
        }

        if (requiresInstrumentDetails(formData.payment_mode) && !formData.reference_no) {
            alert('Reference number is required for Cheque/DD payments.');
            return;
        }

        if (tdsGlRequired && !formData.tds_gl_code) {
            alert('TDS GL account is required when TDS is deducted.');
            return;
        }

        if (totalAllocated > totalSettled + 0.01) {
            alert('Total allocated amount cannot exceed receipt amount plus TDS deducted.');
            return;
        }

        const payload = {
            society_id: societyId,
            flat_id: parseInt(formData.flat_id),
            entity_id: parseInt(formData.entity_id),
            receipt_type: formData.receipt_type,
            payment_date: formData.payment_date,
            amount: parseFloat(formData.amount),
            payment_mode: formData.payment_mode,
            bank_account_id: formData.payment_mode !== 'CASH' && formData.bank_account_id ? parseInt(formData.bank_account_id) : undefined,
            reference_no: formData.reference_no || undefined,
            bank_name: formData.bank_name || undefined,
            instrument_date: formData.instrument_date || undefined,
            tds_deducted: formData.tds_deducted ? parseFloat(formData.tds_deducted) : undefined,
            tds_section: formData.tds_section || undefined,
            tds_rate: formData.tds_rate ? parseFloat(formData.tds_rate) : undefined,
            tds_gl_code: tdsGlRequired ? formData.tds_gl_code : undefined,
            remarks: formData.remarks || undefined,
            payment_proof: proofFile || undefined,
            allocations: allocations
                .filter(a => a.target_id && parseFloat(a.amount) > 0)
                .map(a => ({
                    ...(a.target_type === 'invoice' ? { invoice_id: parseInt(a.target_id) } : { debit_note_id: parseInt(a.target_id) }),
                    amount: parseFloat(a.amount),
                })),
            onSuccess: () => {
                if (tdsGlRequired && formData.tds_gl_code) {
                    localStorage.setItem(tdsGlStorageKey(societyId), formData.tds_gl_code);
                }
                navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_RECEIPTS);
            },
        };

        dispatch(createReceipt(payload));
    };

    return (
        <>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button onClick={() => navigate(-1)} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"><Icon icon="mdi:arrow-left" className="tw-text-xl" /></button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Create Receipt</h1>
                        <p className="tw-text-xs tw-text-gray-500">Record a new payment receipt</p>
                    </div>
                </div>
            </div>

            {/* Series pre-check loading */}
            {seriesCheck.loading && (
                <div className="tw-text-center tw-py-10">
                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                    <p className="tw-text-sm tw-text-gray-500 tw-mt-3">Checking document series configuration...</p>
                </div>
            )}

            {/* No series configured warning */}
            {!seriesCheck.loading && !seriesCheck.exists && (
                <div className="tw-bg-amber-50 tw-border tw-border-amber-300 tw-rounded-xl tw-p-6 tw-text-center">
                    <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-amber-100 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                        <Icon icon="mdi:alert-circle-outline" className="tw-text-3xl tw-text-amber-600" />
                    </div>
                    <h3 className="tw-text-base tw-font-semibold tw-text-gray-800 tw-mb-2">No Receipt Series Configured</h3>
                    <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                        A document series must be set up before creating receipts. Receipt numbers are auto-generated from the configured series.
                    </p>
                    <Link
                        to={SOCIETY_PORTAL + SOCIETY_PORTAL_SETUP_DOCUMENT_SERIES}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-amber-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-amber-700 tw-no-underline"
                    >
                        <Icon icon="mdi:cog-outline" />
                        Set Up Document Series
                    </Link>
                </div>
            )}

            {/* Series preview hint */}
            {!seriesCheck.loading && seriesCheck.exists && seriesCheck.previewNumber && (
                <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-3">
                    <Icon icon="mdi:information-outline" className="tw-text-blue-600 tw-text-lg tw-flex-shrink-0" />
                    <p className="tw-text-sm tw-text-blue-800 tw-mb-0">
                        Next receipt number will be auto-generated: <strong>{seriesCheck.previewNumber}</strong>
                    </p>
                </div>
            )}

            {!seriesCheck.loading && seriesCheck.exists && <form onSubmit={handleSubmit} className="tw-space-y-6">
                {/* Basic Info */}
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:information-outline" className="tw-text-gray-400" /> Basic Information
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Receipt Type *</label>
                            <select name="receipt_type" value={formData.receipt_type} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500">
                                {RECEIPT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Wing *</label>
                            <select name="wing_id" value={formData.wing_id} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500">
                                <option value="">Select Wing</option>
                                {(wingsDropdown || []).map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Flat *</label>
                            <select name="flat_id" value={formData.flat_id} onChange={handleChange} required disabled={!formData.wing_id || flatsWithOwnershipLoading} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-opacity-50">
                                <option value="">{flatsWithOwnershipLoading ? 'Loading...' : 'Select Flat'}</option>
                                {(flatsWithOwnership || []).map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number} - {f.flat_type} ({f.ownership_type})</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Member / Entity *</label>
                            <select name="entity_id" value={formData.entity_id} onChange={handleChange} required disabled={!formData.flat_id} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-opacity-50">
                                <option value="">{!formData.flat_id ? 'Select flat first' : 'Select Member'}</option>
                                {selectedFlatMembers.map(ownership => {
                                    const name = ownership.entity?.entity_type === 'I'
                                        ? `${ownership.entity?.first_name || ''} ${ownership.entity?.last_name || ''}`.trim()
                                        : ownership.entity?.company_name || '-';
                                    return <option key={ownership.entity?.entity_id} value={ownership.entity?.entity_id}>{name} ({ownership.ownership_share}%)</option>;
                                })}
                            </select>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Payment Date *</label>
                            <input type="date" name="payment_date" value={formData.payment_date} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Amount Received (Bank) *</label>
                            <input type="number" name="amount" step="0.01" min="0.01" value={formData.amount} onChange={handleChange} required placeholder="0.00" className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                            <p className="tw-mt-1 tw-text-xs tw-text-gray-400">Money actually banked, excluding any TDS the member withheld.</p>
                        </div>
                        {tdsDeducted > 0 && (
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Total Settled</label>
                                <div className="tw-w-full tw-px-3 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-semibold tw-text-gray-800">
                                    {formatCurrency(totalSettled)}
                                </div>
                                <p className="tw-mt-1 tw-text-xs tw-text-gray-400">Received + TDS. This is what you allocate against invoices.</p>
                            </div>
                        )}
                    </div>

                    {/* Members display */}
                    {formData.flat_id && selectedFlatMembers.length > 0 && (
                        <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                            <p className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">Members / Owners</p>
                            <div className="tw-flex tw-flex-wrap tw-gap-2">
                                {selectedFlatMembers.map(ownership => {
                                    const name = ownership.entity?.entity_type === 'I'
                                        ? `${ownership.entity?.first_name || ''} ${ownership.entity?.last_name || ''}`.trim()
                                        : ownership.entity?.company_name || '-';
                                    const isSelected = formData.entity_id === String(ownership.entity?.entity_id);
                                    return (
                                        <div key={ownership.flat_ownership_id} className={`tw-inline-flex tw-items-center tw-gap-2 tw-pl-1.5 tw-pr-3 tw-py-1 tw-border tw-rounded-full tw-shadow-sm ${isSelected ? 'tw-bg-blue-50 tw-border-blue-300' : 'tw-bg-white tw-border-gray-200'}`}>
                                            <div className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-blue-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Icon icon={ownership.entity?.entity_type === 'I' ? 'mdi:account' : 'mdi:office-building'} className="tw-text-blue-600 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-xs tw-font-semibold tw-text-gray-800">{name}</span>
                                            <span className="tw-text-[10px] tw-font-bold tw-text-blue-600 tw-bg-blue-50 tw-border tw-border-blue-100 tw-px-1.5 tw-py-0.5 tw-rounded-full">{ownership.ownership_share}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Payment Details */}
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:credit-card-outline" className="tw-text-gray-400" /> Payment Details
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Payment Mode *</label>
                            <select name="payment_mode" value={formData.payment_mode} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500">
                                {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        {formData.payment_mode !== 'CASH' && (
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Bank Account *</label>
                            <select
                                name="bank_account_id"
                                value={formData.bank_account_id}
                                onChange={handleChange}
                                required
                                disabled={banksDropdownLoading}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-opacity-50"
                            >
                                <option value="">{banksDropdownLoading ? 'Loading...' : 'Select Bank Account'}</option>
                                {(banksDropdown || []).map(b => (
                                    <option key={b.id} value={b.id}>
                                        {b.bank_name}{b.account_number ? ` - ${b.account_number}` : ''}{b.is_default === 1 ? ' (Default)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        )}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Reference No {requiresInstrumentDetails(formData.payment_mode) ? '*' : ''}</label>
                            <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} placeholder="Cheque No / UTR / Transaction ID" maxLength={100} required={requiresInstrumentDetails(formData.payment_mode)} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                        {requiresInstrumentDetails(formData.payment_mode) && (
                            <>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Bank Name</label>
                                    <input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} placeholder="Issuing bank name" className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Instrument Date</label>
                                    <input type="date" name="instrument_date" value={formData.instrument_date} onChange={handleChange} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Remarks</label>
                            <input type="text" name="remarks" value={formData.remarks} onChange={handleChange} placeholder="Optional remarks..." className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                    </div>

                    {/* Payment Proof */}
                    <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">Payment Proof (Optional)</p>
                        {proofFile ? (
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <div className="tw-w-24 tw-h-24 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    {proofPreview && !isHeicFile(proofFile)
                                        ? <img src={proofPreview} alt="Payment proof preview" className="tw-w-full tw-h-full tw-object-cover" />
                                        : <Icon icon="mdi:file-image-outline" className="tw-text-3xl tw-text-gray-400" />}
                                </div>
                                <div className="tw-min-w-0">
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate tw-mb-0" title={proofFile.name}>{proofFile.name}</p>
                                    <p className="tw-text-xs tw-text-gray-400 tw-mb-0">{(proofFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                                    <div className="tw-flex tw-gap-2 tw-mt-2">
                                        <button type="button" onClick={() => proofInputRef.current?.click()} className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-border tw-border-blue-200 tw-text-blue-600 tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-50">
                                            <Icon icon="mdi:image-edit-outline" /> Replace
                                        </button>
                                        <button type="button" onClick={() => setProofFile(null)} className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-border tw-border-red-200 tw-text-red-600 tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-red-50">
                                            <Icon icon="mdi:trash-can-outline" /> Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                                <button type="button" onClick={() => proofInputRef.current?.click()} className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-dashed tw-border-gray-300 tw-rounded-lg tw-text-sm tw-text-gray-600 hover:tw-border-blue-400 hover:tw-text-blue-600">
                                    <Icon icon="mdi:camera-plus-outline" /> Attach Photo
                                </button>
                                <p className="tw-text-xs tw-text-gray-400 tw-mb-0">UPI/NEFT screenshot or cheque photo. PNG, JPG, WebP or HEIC, up to 5MB.</p>
                            </div>
                        )}
                        <input ref={proofInputRef} type="file" accept={PAYMENT_PROOF_ACCEPT} onChange={handleProofSelect} hidden />
                    </div>

                    {/* TDS Section */}
                    <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">TDS Details (Optional)</p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">TDS Deducted</label>
                                <input type="number" name="tds_deducted" step="0.01" min="0" value={formData.tds_deducted} onChange={handleChange} placeholder="0.00" className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                            </div>
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">TDS Rate %</label>
                                <input type="number" name="tds_rate" step="0.01" min="0" max="100" value={formData.tds_rate} onChange={handleChange} placeholder="0" className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                            </div>
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">TDS Section</label>
                                <input type="text" name="tds_section" value={formData.tds_section} onChange={handleChange} placeholder="e.g. 194C" className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                            </div>
                            <div className="md:tw-col-span-2">
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                    TDS GL Account {tdsGlRequired && <span className="tw-text-red-500">*</span>}
                                </label>
                                <SearchableDropdown
                                    value={formData.tds_gl_code}
                                    onChange={(val) => setFormData(prev => ({ ...prev, tds_gl_code: val || '' }))}
                                    options={coaDropdown || []}
                                    valueKey="ac_code"
                                    labelKey="label"
                                    placeholder={tdsGlRequired ? 'Select TDS receivable account' : 'Enter a TDS amount first'}
                                    disabled={!tdsGlRequired || coaDropdownLoading}
                                    loading={coaDropdownLoading}
                                    small
                                />
                                <p className={`tw-mt-1 tw-text-xs ${tdsGlRequired && !formData.tds_gl_code ? 'tw-text-red-600' : 'tw-text-gray-400'}`}>
                                    {tdsGlRequired && !formData.tds_gl_code
                                        ? 'Required when TDS is deducted.'
                                        : "Use the society's TDS Receivable account - the deducted amount is claimed back in the return."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Allocations - hidden for Advance receipts */}
                {formData.receipt_type !== 'ADVANCE' && <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:format-list-bulleted" className="tw-text-gray-400" /> Allocations
                        </h2>
                        <div className="tw-flex tw-gap-2">
                            <button type="button" onClick={() => addAllocation('invoice')} disabled={!formData.flat_id} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50">
                                <Icon icon="mdi:plus" /> Invoice
                            </button>
                            <button type="button" onClick={() => addAllocation('debit_note')} disabled={!formData.flat_id} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-bg-orange-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-orange-700 disabled:tw-opacity-50">
                                <Icon icon="mdi:plus" /> Debit Note
                            </button>
                        </div>
                    </div>

                    {/* Amount summary bar */}
                    {receiptAmount > 0 && (
                        <div className="tw-mb-4 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-between tw-text-sm">
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <span className="tw-text-gray-500">Received: <span className="tw-font-semibold tw-text-gray-800">{formatCurrency(receiptAmount)}</span></span>
                                {tdsDeducted > 0 && (
                                    <>
                                        <span className="tw-text-gray-500">TDS: <span className="tw-font-semibold tw-text-indigo-600">{formatCurrency(tdsDeducted)}</span></span>
                                        <span className="tw-text-gray-500">Settled: <span className="tw-font-semibold tw-text-gray-800">{formatCurrency(totalSettled)}</span></span>
                                    </>
                                )}
                                <span className="tw-text-gray-500">Allocated: <span className="tw-font-semibold tw-text-blue-600">{formatCurrency(totalAllocated)}</span></span>
                            </div>
                            <span className={`tw-font-semibold ${unallocated < -0.01 ? 'tw-text-red-600' : unallocated > 0.01 ? 'tw-text-amber-600' : 'tw-text-emerald-600'}`}>
                                {unallocated < -0.01 ? 'Over-allocated' : unallocated > 0.01 ? `Unallocated: ${formatCurrency(unallocated)}` : 'Fully Allocated'}
                            </span>
                        </div>
                    )}

                    {allocations.length > 0 ? (
                        <div className="tw-space-y-4">
                            {allocations.map((alloc, allocIdx) => {
                                const isInvoice = alloc.target_type === 'invoice';
                                const usedIds = allocations
                                    .filter((a, i) => i !== allocIdx && a.target_type === alloc.target_type && a.target_id)
                                    .map(a => a.target_id);
                                const targetOptions = (isInvoice ? (outstandingInvoices || []) : (outstandingDebitNotes || []))
                                    .filter(t => isInvoice ? t.is_carried_forward !== 1 : true)
                                    .filter(t => !usedIds.includes(String(isInvoice ? t.invoice_id : t.note_id)));
                                const selectedTarget = (isInvoice ? (outstandingInvoices || []) : (outstandingDebitNotes || []))
                                    .find(t => String(isInvoice ? t.invoice_id : t.note_id) === alloc.target_id);

                                return (
                                    <div key={allocIdx} className={`tw-border tw-rounded-lg tw-overflow-hidden ${isInvoice ? 'tw-border-blue-200' : 'tw-border-orange-200'}`}>
                                        {/* Allocation header */}
                                        <div className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 ${isInvoice ? 'tw-bg-blue-50' : 'tw-bg-orange-50'}`}>
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <Icon icon={isInvoice ? 'mdi:file-document-outline' : 'mdi:note-minus-outline'} className={`tw-text-base ${isInvoice ? 'tw-text-blue-600' : 'tw-text-orange-600'}`} />
                                                <span className="tw-text-xs tw-font-semibold tw-text-gray-700">{isInvoice ? 'Invoice Allocation' : 'Debit Note Allocation'}</span>
                                                {alloc.amount && <span className="tw-text-xs tw-font-bold tw-text-gray-800 tw-ml-2">{formatCurrency(alloc.amount)}</span>}
                                            </div>
                                            <button type="button" onClick={() => removeAllocation(allocIdx)} className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-gray-400 hover:tw-text-red-600"><Icon icon="mdi:close" /></button>
                                        </div>

                                        <div className="tw-p-4 tw-space-y-3">
                                            {/* Target selection + Amount */}
                                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                                                <div>
                                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">{isInvoice ? 'Select Invoice' : 'Select Debit Note'} *</label>
                                                    <select
                                                        value={alloc.target_id}
                                                        onChange={(e) => updateAllocation(allocIdx, 'target_id', e.target.value)}
                                                        disabled={outstandingLoading}
                                                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-opacity-50"
                                                    >
                                                        <option value="">{outstandingLoading ? 'Loading...' : `Select ${isInvoice ? 'Invoice' : 'Debit Note'}`}</option>
                                                        {targetOptions.map(t => (
                                                            <option key={isInvoice ? t.invoice_id : t.note_id} value={isInvoice ? t.invoice_id : t.note_id}>
                                                                {t.document_number} - Balance: {formatCurrency(t.balance_due)} ({t.status})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Amount *</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        value={alloc.amount}
                                                        onChange={(e) => updateAllocation(allocIdx, 'amount', e.target.value)}
                                                        placeholder="0.00"
                                                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-right focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                                    />
                                                </div>
                                            </div>

                                            {/* Selected target info */}
                                            {selectedTarget && (
                                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-grid tw-grid-cols-3 tw-gap-2 tw-text-xs">
                                                    <div><span className="tw-text-gray-400">Total:</span> <span className="tw-font-semibold">{formatCurrency(selectedTarget.net_payable)}</span></div>
                                                    <div><span className="tw-text-gray-400">Paid:</span> <span className="tw-font-semibold">{formatCurrency(selectedTarget.amount_paid)}</span></div>
                                                    <div><span className="tw-text-gray-400">Balance:</span> <span className="tw-font-semibold tw-text-red-600">{formatCurrency(selectedTarget.balance_due)}</span></div>
                                                </div>
                                            )}

                                            {/* Component breakdown preview */}
                                            {(() => {
                                                const compKey = isInvoice ? `invoice_${alloc.target_id}` : `debit_note_${alloc.target_id}`;
                                                const components = componentsMap[compKey];
                                                if (!components?.length) return null;
                                                return (
                                                    <div className="tw-p-3 tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg">
                                                        <p className="tw-text-[11px] tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wider tw-mb-2">Component Breakdown</p>
                                                        <div className="tw-flex tw-flex-wrap tw-gap-3">
                                                            {components.map((comp, ci) => (
                                                                <div key={ci} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
                                                                    <span className="tw-text-gray-500">{comp.component}:</span>
                                                                    <span className="tw-font-semibold tw-text-gray-800">{formatCurrency(comp.amount)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Preview Component Breakdown button */}
                            {validAllocations.length > 0 && (
                                <div className="tw-mt-4 tw-flex tw-justify-end">
                                    <button
                                        type="button"
                                        onClick={handlePreviewComponents}
                                        disabled={componentsLoading}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-300 tw-rounded-lg tw-text-xs tw-font-medium hover:tw-bg-emerald-100 disabled:tw-opacity-50"
                                    >
                                        <Icon icon={componentsLoading ? "mdi:loading" : "mdi:calculator-variant-outline"} className={componentsLoading ? 'tw-animate-spin' : ''} />
                                        {componentsLoading ? 'Calculating...' : 'Preview Component Breakdown'}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="tw-text-center tw-py-8 tw-text-gray-400">
                            <Icon icon="mdi:receipt-text-outline" className="tw-text-4xl tw-mb-2" />
                            <p className="tw-text-sm">{formData.flat_id ? 'No allocations added. Click above to allocate against invoices or debit notes.' : 'Select a flat first to add allocations.'}</p>
                        </div>
                    )}
                </div>}

                {/* Submit Actions */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <button type="button" onClick={() => navigate(-1)} className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-300">Cancel</button>
                    <button type="submit" disabled={createLoading} className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-emerald-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-emerald-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                        <Icon icon={createLoading ? "mdi:loading" : "mdi:check"} className={createLoading ? 'tw-animate-spin' : ''} />
                        {createLoading ? 'Creating...' : 'Create Receipt'}
                    </button>
                </div>
            </form>}
        </>
    );
};

export default CreateReceipt;
